import React, {
  type FC,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { RememberedUser } from "@components/RememberMe/contract";
import useDeleteRememberMeCookies from "clientState/useDeleteRememberMeCookies";
import { useRememberBooking } from "clientState/useRememberBooking";
import { LOGIN_METHODS, useRememberMe } from "clientState/useRememberMe";
import { useRememberPrimeBooking } from "clientState/useRememberPrimeBooking";

import {
  type AsyncRecognizedUserState,
  type RecognizedUserAuthorizationApi,
  type RecognizedUserProviderState,
  type RecognizedUserProviderWithoutBookingCheck,
  UserRecognitionStrategy,
} from "./contract";
import RecognizedUserContext from "./RecognizedUserContext";
import RecognizedUserWithPostbookingEmailMissmatchMiddlewareProvider from "./RecognizedUserWithPostbookingEmailMissmatchMiddlewareProvider";
import useDecryptCrmUserToken from "./useDecryptCrmUserToken/useDecryptCrmUserToken";
import useRecognizeUser from "./useRecognizeUser";

export const RecognizedUserProvider: FC<
  RecognizedUserProviderWithoutBookingCheck
> = ({ authToken, onTokenDecryptError, postBookingEmail, children }) => {
  const deleteRememberMeCookies = useDeleteRememberMeCookies();
  const [getRememberedUser, setRememberedUser] = useRememberMe();

  const [
    needsUserRecognitionAuthorization,
    setNeedsUserRecognitionAuthorization,
  ] = useState(() => {
    const rememberedMeUser = getRememberedUser() as unknown as RememberedUser;
    return (
      rememberedMeUser === null ||
      rememberedMeUser.loginMethod === LOGIN_METHODS.recognized
    );
  });
  const [rejectedUserAuthorization, setRejectedUserAuthorization] =
    useState(false);

  const asyncDecryptedToken = useDecryptCrmUserToken({
    token: authToken,
    skip: !authToken,
    onError: onTokenDecryptError,
  });

  // Remember booking source for user recognition
  const rememberBookingUser = useRememberBooking();
  // Remember prime booking source for user recognition
  const rememberPrimeBookingUser = useRememberPrimeBooking();

  const recognizedUser = useRecognizeUser(
    asyncDecryptedToken,
    rememberBookingUser,
    rememberPrimeBookingUser,
  );

  const handleUnauthorizeUser = useCallback(() => {
    deleteRememberMeCookies();
    setRejectedUserAuthorization(true);
    setNeedsUserRecognitionAuthorization(false);
  }, [deleteRememberMeCookies]);

  const handleAuthorizeUser = useCallback(() => {
    setNeedsUserRecognitionAuthorization(false);
    const rememberedUser = getRememberedUser() as unknown as RememberedUser;
    if (rememberedUser) {
      setRememberedUser(LOGIN_METHODS.password, rememberedUser);
    }
  }, [
    setNeedsUserRecognitionAuthorization,
    getRememberedUser,
    setRememberedUser,
  ]);

  const isCRMEmailValidationMissmatchWithSSORememberMe = useMemo(() => {
    const rememberMeUser = getRememberedUser() as unknown as RememberedUser;
    return (
      recognizedUser?.recognitionStrategy === UserRecognitionStrategy.CRM &&
      rememberMeUser !== null &&
      rememberMeUser.email !== recognizedUser.email
    );
  }, [getRememberedUser, recognizedUser]);

  const state = useMemo(() => {
    return {
      user: recognizedUser
        ? {
            ...recognizedUser,
            isPrime: rejectedUserAuthorization ? false : recognizedUser.isPrime,
          }
        : null,
      loading: asyncDecryptedToken.loading,
      errors: asyncDecryptedToken.errors,
      needsUserRecognitionAuthorization:
        (needsUserRecognitionAuthorization && recognizedUser !== null) ||
        isCRMEmailValidationMissmatchWithSSORememberMe,
      onUnauthorizeUser: handleUnauthorizeUser,
      onAuthorizeUser: handleAuthorizeUser,
    };
  }, [
    recognizedUser,
    rejectedUserAuthorization,
    asyncDecryptedToken.loading,
    asyncDecryptedToken.errors,
    needsUserRecognitionAuthorization,
    isCRMEmailValidationMissmatchWithSSORememberMe,
    handleUnauthorizeUser,
    handleAuthorizeUser,
  ]);

  return (
    <RecognizedUserWithPostbookingEmailMissmatchMiddlewareProvider
      postBookingEmail={postBookingEmail}
      userRecognitionState={state}
    >
      {children}
    </RecognizedUserWithPostbookingEmailMissmatchMiddlewareProvider>
  );
};

/**
 * Users that are recognized alternative cookies (like booking cookies, NOT the sso_remember_me cookie) or by CRM (auth_token)
 */
export function useRecognizedUser(): AsyncRecognizedUserState | undefined {
  const state = useContext(RecognizedUserContext);
  return useMemo(() => {
    if (state === undefined) {
      return undefined;
    }

    return {
      loading: state.loading,
      errors: state.errors,
      user: state.user,
      needsUserRecognitionAuthorization:
        state.needsUserRecognitionAuthorization,
    };
  }, [state]);
}

function noop(): void {}
/**
 * This hook provides the methods to unauthorize and authorize the user
 * when the user is recognized but we need user feedback to validate the user is actually them.
 */
export function useRecognizedUserAuthorization(): RecognizedUserAuthorizationApi {
  const state = useContext(RecognizedUserContext);

  const onUnauthorizeUser = useMemo(
    () => state?.onUnauthorizeUser ?? noop,
    [state?.onUnauthorizeUser],
  );
  const onAuthorizeUser = useMemo(
    () => state?.onAuthorizeUser ?? noop,
    [state?.onAuthorizeUser],
  );

  return {
    onUnauthorizeUser,
    onAuthorizeUser,
  };
}

export function useUserRecognitionState():
  | RecognizedUserProviderState
  | undefined {
  return useContext(RecognizedUserContext);
}
